import { AxiosPromise } from 'axios';
import Http from '../../shared/http';
import { ServerResponse } from '../types';
import {
  API_PATH_EWARRANT,
  API_PATH_EWARRANT_AUTH_STATUS,
  API_PATH_EWARRANT_DETAIL,
  API_PATH_EWARRANT_DOCUMENT_DOWNLOAD,
  API_PATH_EWARRANT_GENERATE,
  API_PATH_EWARRANT_INTERCEPT_STATUS,
  API_PATH_EWARRANT_TASK_STATUS,
  EWarrantAuthStatusPayload,
  EWarrantDetailResponse,
  EWarrantGenerateRequest,
  EWarrantGeneration,
  EWarrantInterceptStatusPayload,
  EWarrantListItem,
  EWarrantTaskStatusPayload,
} from './eWarrant.types';

const http = Http.getHttp();

/**
 * Reads the e-warrants Xcipio holds against its EWSAUTH generic resource. The
 * endpoint is not scoped to a delivery function — the backend resolves the Xcipio
 * it talks to itself, the same way the generate and intercept-status calls do.
 *
 * @param query the already built `$top=…&$skip=…&$orderBy=…&$filter=…` the grid asks
 *              for, passed straight through the way every other server side grid read
 *              in this codebase does it (see getAllWarrants). Empty reads everything.
 */
export function getEWarrantList(query = ''): AxiosPromise<ServerResponse<EWarrantListItem>> {
  return http.get(query ? `${API_PATH_EWARRANT}?${query}` : API_PATH_EWARRANT);
}

/**
 * Reads one e-warrant in full — its documents and LI tasks included. The list read
 * carries neither, so the view and the generate flow both depend on this.
 */
export function getEWarrantDetail(
  hi1ObjectId: string,
  senderId: string,
): AxiosPromise<ServerResponse<EWarrantDetailResponse>> {
  // SENDERID and HI1OBJECTID together key every one of the three tables the backend
  // reads, so both travel.
  return http.get(API_PATH_EWARRANT_DETAIL, { params: { hi1ObjectId, senderId } });
}

/**
 * First step of the generate warrant flow — moves the intercept status of the
 * e-warrant. Only its id travels: the backend reads the EWSAUTH row out of Xcipio
 * itself, since that is what Xcipio takes back on an update.
 */
export function updateInterceptStatus(
  payload: EWarrantInterceptStatusPayload,
): AxiosPromise<ServerResponse<void>> {
  return http.put(API_PATH_EWARRANT_INTERCEPT_STATUS, payload);
}

/**
 * Records the operator's decision — moves the authorisation status of the e-warrant and
 * carries the decision note as the message explaining it, along with the decision made on
 * each of its documents. As with the intercept status, only the ids travel: the backend
 * reads the EWSAUTH and EWSDOCUMENT rows out of Xcipio itself.
 */
export function updateAuthStatus(
  payload: EWarrantAuthStatusPayload,
): AxiosPromise<ServerResponse<void>> {
  return http.put(API_PATH_EWARRANT_AUTH_STATUS, payload);
}

/**
 * Records the decision an operator made against one target — moves the status of that
 * target's LI task. As with the authorisation status, only the ids travel: the backend
 * reads the EWSLITASK row out of Xcipio itself and writes the whole row back.
 */
export function updateTaskStatus(
  payload: EWarrantTaskStatusPayload,
): AxiosPromise<ServerResponse<void>> {
  return http.put(API_PATH_EWARRANT_TASK_STATUS, payload);
}

/**
 * Asks the backend to generate the CSP side warrant. The payload carries the
 * e-warrant as the portal holds it, together with the collection functions built
 * from the templates its handover formats map onto.
 */
export function generateWarrant(
  request: EWarrantGenerateRequest,
): AxiosPromise<ServerResponse<EWarrantGeneration>> {
  return http.post(API_PATH_EWARRANT_GENERATE, request);
}

/**
 * Downloads one e-warrant document's file. As with updateAuthStatus, only the ids
 * travel — the backend reads the EWSDOCUMENT row out of Xcipio itself and streams its
 * file straight back.
 */
export function downloadEWarrantDocument(senderId: string, docId: string): AxiosPromise<BlobPart> {
  return http.get(API_PATH_EWARRANT_DOCUMENT_DOWNLOAD, {
    params: { senderId, docId },
    responseType: 'blob',
  });
}
